import { Button, Col, Form, InputNumber, Modal, ModalProps, notification, Radio, Row, Select, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import LowGrainShutOffMode from "src/consts/LowGrainShutOffMode";
import DryingOperationRequestDTO from "src/models/DryingOperationRequestDTO";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { generateSweepOffTimeOptions, getEnumDictionary, getEnumKeys, getGrainTypeStr, getSweepOffTimeLabel } from "src/utils/enumUtils";
import GrainType from "src/consts/GrainType";
import { convertCelsiusToFahrenheit, convertCelsiusToFahrenheitOffset, convertFahrenheitToCelsius, convertFahrenheitToCelsiusOffset } from "src/utils/generalUtils";
import { formatNumber } from "src/utils/formatting";
import BinApiService from "src/api/BinApiService";

const { Text, Title } = Typography;
const { Option } = Select;

interface PremierPlusDryerSettingsModalProps extends ModalProps {
    binDto: PremierPlusBinDTO | null;
    onClose: (result: any) => void;
}

const PremierPlusDryerSettingsModalV2 : React.FC<PremierPlusDryerSettingsModalProps> = (props) => {

    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm<DryingOperationRequestDTO>();
    const values = Form.useWatch([], form);

    const grainOptions = getEnumDictionary(GrainType);
    const sweepOffTimeOptions = generateSweepOffTimeOptions();
    const useCelsius = props.binDto?.platformSettings?.useMetricSystem;

    const temperatureUnit = useCelsius ? "°C" : "°F";
    const tempButtonStep = useCelsius ? 1 : 2;

    const toDisplayTemperature = (value: number | null | undefined) =>
        useCelsius ? convertFahrenheitToCelsius(value) : value ?? null;

    const toServerTemperature = (value: number | null | undefined) =>
        useCelsius ? convertCelsiusToFahrenheit(value) : value ?? null;

    const toDisplayTemperatureOffset = (value: number | null | undefined) =>
        useCelsius ? convertFahrenheitToCelsiusOffset(value) : value ?? null;

    const toServerTemperatureOffset = (value: number | null | undefined) =>
        useCelsius ? convertCelsiusToFahrenheitOffset(value) : value ?? null;

    
    useEffect(() => {

        if(form.isFieldsTouched()){
            return;
        }

        form.setFieldValue('grainType', props.binDto?.settings?.dryerSettings?.grainType);
        form.setFieldValue('lgsoMode', props.binDto?.settings?.dryerSettings?.lgsoMode);

        form.setFieldValue('minPlenumTemperatureF', toDisplayTemperature(props.binDto?.settings?.dryerSettings?.minPlenumTemperatureF));
        form.setFieldValue('maxPlenumTemperatureF', toDisplayTemperature(props.binDto?.settings?.dryerSettings?.maxPlenumTemperatureF));
        form.setFieldValue('targetPlenumTemperatureF', toDisplayTemperature(props.binDto?.settings?.dryerSettings?.targetPlenumTemperatureF));

        form.setFieldValue('samplesCntInAverage', props.binDto?.settings?.dryerSettings?.samplesCntInAverage);
        form.setFieldValue('targetMcPercent', props.binDto?.settings?.dryerSettings?.targetMcPercent);
        form.setFieldValue('sweepOffTime', props.binDto?.settings?.dryerSettings?.sweepOffTime);
        form.setFieldValue('timedBypassMinutes', props.binDto?.settings?.dryerSettings?.timedBypassMinutes);
        form.setFieldValue('minutesToFirstSample', props.binDto?.settings?.dryerSettings?.minutesToFirstSample);
        form.setFieldValue('fanShutOffDelayMinutes', props.binDto?.settings?.dryerSettings?.fanShutOffDelayMinutes);
        form.setFieldValue('disableMoistureChangingCheck', props.binDto?.settings?.dryerSettings?.disableMoistureChangingCheck);

        form.setFieldValue('grainMcCalibrationPercent', toDisplayTemperatureOffset(props.binDto?.settings?.dryerSettings?.grainMcCalibrationPercent));
        form.setFieldValue('grainTempCalibrationF', toDisplayTemperatureOffset(props.binDto?.settings?.dryerSettings?.grainTempCalibrationF));
        form.setFieldValue('plenumTempCalibrationF', toDisplayTemperatureOffset(props.binDto?.settings?.dryerSettings?.plenumTempCalibrationF));    

    }, [form, props.binDto, useCelsius]);


    const onFinish = async (data: DryingOperationRequestDTO) => 
    {
        try{
            setIsLoading(true);
            try {
                await BinApiService.premierPlus_UpdateDryingOperation(props.binDto?.binId ?? 0, data);
                props.onClose(null);
            } catch (err) {
                notification.error({ message: "Could not set dryer settings" });
                console.error(err);
            }
        }catch(error){
            notification.error({ message: "Could not set dryer settings" });
            console.error(error);
        }finally{
            setIsLoading(false);
        }
    }

    const handleResetBypassClicked = () => {
        form.setFieldValue("timedBypassMinutes", 0.5);
    };


    const handleClose = () => {
        props.onClose(null);
    };

    // actual temp + calibration
    const calibratedGrainTempF = useMemo(() => {
        if (!props.binDto?.dryingOperation?.isRunning) return null;
        const base = props.binDto?.grainTempF;
        if (base == null) return null;
        return base + values?.grainTempCalibrationF;

    }, [props.binDto?.grainTempF, props.binDto?.dryingOperation?.isRunning, values?.grainTempCalibrationF]);

    const calibratedMcPercent = useMemo(() => {
        if (!props.binDto?.dryingOperation?.isRunning) return null;
        const base = props.binDto?.dryingOperation?.targetMcPercent;
        if (base == null) return null;
        return base + values?.targetMcPercent;

    }, [props.binDto?.dryingOperation?.targetMcPercent, props.binDto?.dryingOperation?.isRunning, values?.targetMcPercent]);

    const calibratedPlenumTempF = useMemo(() => {
        if (!props.binDto?.dryingOperation?.isRunning) return null;
        const base = props.binDto?.plenumTempF;
        if (base == null) return null;
        return base + values?.plenumTempCalibrationF;

    }, [props.binDto?.plenumTempF, props.binDto?.dryingOperation?.isRunning, values?.plenumTempCalibrationF]);

    const formatDisplayValue = (value: number | null | undefined, suffix: string) =>
        value == null ? `--${suffix}` : `${value}${suffix}`;

    const handleIncreaseGrainTemp = () => {
        const current = form.getFieldValue("grainTempCalibrationF") ?? 0;
        form.setFieldValue("grainTempCalibrationF", current + tempButtonStep);
    };

    const handleDecreaseGrainTemp = () => {
        const current = form.getFieldValue("grainTempCalibrationF") ?? 0;
        form.setFieldValue("grainTempCalibrationF", current - tempButtonStep);
    };

    const handleIncreaseGrainMc = () => {
        const current = form.getFieldValue("grainMcCalibrationPercent") ?? 0;
        form.setFieldValue("grainMcCalibrationPercent", current + 2);
    };

    const handleDecreaseGrainMc = () => {
        const current = form.getFieldValue("grainMcCalibrationPercent") ?? 0;
        form.setFieldValue("grainMcCalibrationPercent", current - 2);
    };

    const handleIncreasePlenumTemp = () => {
        const current = form.getFieldValue("plenumTempCalibrationF") ?? 0;
        form.setFieldValue("plenumTempCalibrationF", current + tempButtonStep);
    };

    const handleDecreasePlenumTemp = () => {
        const current = form.getFieldValue("plenumTempCalibrationF") ?? 0;
        form.setFieldValue("plenumTempCalibrationF", current - tempButtonStep);
    };

    return(
        <Modal
        {...props}
        title={<p>Auger Settings</p>}
        destroyOnClose 
        footer={null}
        width="100%" 
        style={{ maxWidth: 800 }} 
        >
            <Spin spinning={isLoading}>
                <Form form={form} onFinish={onFinish}>
                    <Row justify={"start"}>
                        <Title style={{ margin: "0px", paddingLeft: "10px", paddingTop: "10px" }} level={3}>
                            Adjust Dryer Settings 
                        </Title>
                    </Row>

                    <br/>
                    <Row gutter={10} justify={'center'} style={{ margin: "5px" }}>
                        <Col span={8}>
                            <Form.Item 
                            name={"maxPlenumTemperatureF"} 
                            label={"Max Temp"}>
                                <InputNumber 
                                inputMode="decimal" 
                                controls={false} 
                                suffix={ useCelsius ? '°C' : '°F' } 
                                style={{ width: '100%' }} 
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={"minutesToFirstSample"} label={"Min to samp:"}>
                                <InputNumber controls={false} inputMode="decimal" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={"samplesCntInAverage"} label={"Num samp in avg:"}>
                                <InputNumber controls={false} inputMode="decimal" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <br/>
                    <Row gutter={10} style={{ margin: '5px' }}>
                        <Col span={8}>
                            <Form.Item  
                            name={"minPlenumTemperatureF"}
                            label="Min Temp:"
                            className="form-item">
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                suffix={ useCelsius ? '°C' : '°F'  }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item 
                            name={"fanShutOffDelayMinutes"}
                            className="form-item"
                            label={"Fan Shut Min:"}
                            >
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item 
                            name={"targetMcPercent"}
                            className="form-item"
                            label={"Transfer Moist:"}>
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                suffix="%"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <br/>
                    <Row gutter={10} style={{ margin:"5px" }}>
                        <Col span={8}>
                            <Form.Item 
                            name={"targetPlenumTemperatureF"}
                            className="form-item"
                            label="Starting target Temp:"
                            >
                                <InputNumber 
                                controls={false}
                                inputMode="decimal"
                                suffix={useCelsius ? '°C' : '°F'  }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <br/>
                    {
                        (
                            <>
                                <Row justify={"start"} gutter={8} style={{ paddingLeft: "10px" }}>
                                    <Col>
                                        <Form.Item
                                        label={<Text>LGSO Mode</Text>}
                                        required={false}
                                        style={{ margin: 0, padding: 0 }}
                                        name={"lgsoMode"}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: ""
                                                }
                                            ]}
                                        >
                                            <Radio.Group buttonStyle={"solid"} defaultValue={false} optionType="button">
                                                <Radio value={LowGrainShutOffMode.Auto}>
                                                    AUTO
                                                </Radio>
                                                <Radio value={LowGrainShutOffMode.TimedBypass}>
                                                    TIMED BYPASS
                                                </Radio>
                                                <Radio value={LowGrainShutOffMode.Bypass}>
                                                    BYPASS
                                                </Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                    {values?.lgsoMode === LowGrainShutOffMode.TimedBypass && (
                                        <Col>
                                            <Row gutter={8}>
                                                <Col>
                                                    <Form.Item
                                                    style={{ margin: 0, padding: 0 }}
                                                    name={"timedBypassMinutes"}
                                                    required={false}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: ""
                                                        }
                                                    ]}
                                                    label={<Text>Timed Bypass minutes</Text>}
                                                    >
                                                        <InputNumber
                                                        style={{ width: "75px" }}
                                                        controls={false}
                                                        inputMode="decimal"
                                                        placeholder="Minutes..."
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col>
                                                    <Button onClick={handleResetBypassClicked}>RESET</Button>
                                                </Col>
                                            </Row>
                                            <Row justify={"center"}>
                                                <Text style={{ fontSize: "12px" }} strong>
                                                    Timed Bypass only counts down while the machine is on
                                                </Text>
                                            </Row>
                                        </Col>
                                    )}
                                </Row>
                                <br/>
                            </>
                        )
                    }
                    <Row style={{ paddingLeft:'10px'}}>
                        <Col span={13}>
                            <Form.Item
                            name={"disableMoistureChangingCheck"}
                            label={<Text>Disable Check for Moisture Not Changing</Text>}
                            valuePropName="checked"
                            style={{ margin:0, padding:0 }}
                            >
                                <Radio.Group
                                buttonStyle={'solid'} 
                                defaultValue={false} 
                                optionType="button" 
                                >
                                    <Radio value={false}>NO</Radio>
                                    <Radio value={true}>YES</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify={'center'}>
                        <Col span={12}>
                            <Form.Item 
                            labelCol={{ span: 24}}
                            style={{ margin: 0, padding: 0}}
                            label={'Sweep off time adjust'}
                            name={"sweepOffTime"}
                            className="form-item"
                            >
                                <Select
                                style={{ width:"250px" }}
                                dropdownStyle={{ 
                                    borderRadius:'0px',
                                    backgroundColor: 'white' 
                                }}
                                >
                                    {sweepOffTimeOptions.map((gt) => (
                                        <Option key={gt.value} value={gt.value}>
                                            <span className="select-fantype-text">{gt.label} min.</span>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>  
                        </Col>
                        <Col>
                            <Form.Item
                                labelCol={{ span: 24}}
                            style={{ margin: 0, padding: 0}}
                            label={'Grain Type'}
                            name={"grainType"}
                            className="form-item"
                            >
                                <Select
                                style={{ width:"250px" }}
                                dropdownStyle={{ 
                                    borderRadius:'0px',
                                    backgroundColor: 'white' 
                                }}
                                >
                                    {grainOptions.map((gt) => (
                                        <Option key={gt.value} value={gt.value}>
                                            <span className="select-fantype-text">{getGrainTypeStr(gt.label)}</span>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>    
                        </Col>
                    </Row>

                    <Row justify={'center'}>
                        <Col span={20} style={{ textAlign: 'center' }}>
                            <Text>
                                Adjust the grain temp cal, and the plenum temp cal by tapping up/down. the calibration will be shown on the right and the adjusted value will be on the left
                            </Text>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={10} style={{ padding: "4px" }}>
                        <Col span={4}>
                            <div className={"non-input-bottom-row"}>Grain Temp:</div>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                {!props.binDto?.dryingOperation?.isRunning ? "--" : formatDisplayValue(calibratedGrainTempF, temperatureUnit)}
                            </div>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleDecreaseGrainTemp}>GRAIN DOWN</Button>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleIncreaseGrainTemp}>GRAIN UP</Button>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                <>
                                    <Form.Item name="grainTempCalibrationF" hidden />
                                    <p>{formatDisplayValue(values?.grainTempCalibrationF, temperatureUnit)}</p>
                                </>
                            </div>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={10} style={{ padding: "4px" }}>
                        <Col span={4}>
                            <div className={"non-input-bottom-row"}>Moisture:</div>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                {!props.binDto?.dryingOperation?.isRunning ? "--" : formatDisplayValue(calibratedMcPercent, "%")}
                            </div>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleDecreaseGrainMc}>MOIST DOWN</Button>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleIncreaseGrainMc}>MOIST UP</Button>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                <>
                                    <Form.Item name="grainMcCalibrationPercent" hidden />
                                    <p>{formatDisplayValue(values?.grainMcCalibrationPercent, "%")}</p>
                                </>
                            </div>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={10} style={{ padding: "4px" }}>
                        <Col span={4}>
                            <div className={"non-input-bottom-row"}>Plenum Temp:</div>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                {!props.binDto?.dryingOperation?.isRunning ? "--" : formatDisplayValue(calibratedPlenumTempF, temperatureUnit)}
                            </div>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleDecreasePlenumTemp}>PLENUM TEMP DOWN</Button>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleIncreasePlenumTemp}>PLENUM TEMP UP</Button>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                <>
                                    <Form.Item name="plenumTempCalibrationF" hidden />
                                    <p>{formatDisplayValue(values?.plenumTempCalibrationF, temperatureUnit)}</p>
                                </>
                            </div>
                        </Col>
                    </Row>

                    <Row justify={"end"} gutter={12}>
                        <Col>
                            <Button onClick={handleClose}>Cancel</Button>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button htmlType="submit" type="primary">
                                    Submit
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    )
}

export default PremierPlusDryerSettingsModalV2;